/**
 * Sentry `beforeSend` filter.
 *
 * Drops browser/OS-level noise that is not actionable from application code and
 * only consumes Sentry quota. Kept as a pure, exported function so the matching
 * rules are unit-testable without booting Sentry.
 */

import type { ErrorEvent, EventHint, Exception } from "@sentry/react"

// iOS Safari throws NotReadableError ("The I/O read operation failed.") for
// transient media/file I/O failures (photo/video decode, file reads) as an
// unhandledrejection with NO JS stack. Not fixable in app code.
// RITUALITY-WEB-1C / RITUALITY-WEB-1H.
const NOT_READABLE_ERROR = /NotReadableError/i

/**
 * Drop only the stack-less iOS NotReadableError noise. A genuine app-code
 * NotReadableError (e.g. FileReader / getUserMedia in a feature we own) carries
 * a stacktrace, so the no-frames check keeps real bugs reportable rather than
 * masking every error of this class.
 */
const isStacklessNotReadableError = (value: Exception): boolean => {
  const haystack = `${value.type ?? ""}: ${value.value ?? ""}`
  const hasFrames = (value.stacktrace?.frames?.length ?? 0) > 0
  return NOT_READABLE_ERROR.test(haystack) && !hasFrames
}

/** True when the event is known browser/OS noise that should not be reported. */
export function isIgnoredSentryEvent(event: ErrorEvent): boolean {
  return (event.exception?.values ?? []).some(isStacklessNotReadableError)
}

/** Sentry `beforeSend` hook: return null to drop the event, else pass it through. */
export function beforeSendFilter(event: ErrorEvent, _hint?: EventHint): ErrorEvent | null {
  return isIgnoredSentryEvent(event) ? null : event
}
